/**
 * Shared API error normalization for the whole app.
 *
 * Extracts a human-readable message from any API failure:
 *  - network / transport errors (fetch rejection, timeout, connection lost)
 *  - GraphQL errors (graphql-request ClientError: response.errors, serialized
 *    JSON messages, errors[], graphQLErrors[])
 *  - REST errors (response.data.message / response.data.error)
 *  - plain Error.message, then a generic fallback as a last resort.
 *
 * Used by the global React Query error handler and by feature code.
 */
import type { TFunction } from 'i18next';

const hasMessage = (entry: unknown): boolean => (
  !!entry && typeof (entry as Record<string, unknown>).message === 'string' && !!(entry as Record<string, unknown>).message
);

const hasGraphQLErrors = (response: Record<string, unknown>): boolean => (
  Array.isArray(response.errors) && response.errors.some(hasMessage)
);

const hasRestBody = (response: Record<string, unknown>): boolean => {
  const { data } = response;
  if (typeof data === 'string') return !!data && data !== '[object Object]';
  if (!data || typeof data !== 'object') return false;
  const body = data as Record<string, unknown>;
  return (
    (typeof body.message === 'string' && !!body.message)
    || (typeof body.error === 'string' && !!body.error)
    || hasGraphQLErrors(body)
  );
};

/**
 * Error whose `.message` is a serialized `{ response: { errors: [...] } }` payload.
 * The server answered, so the substring heuristics below must not run on it.
 */
const hasSerializedServerError = (message: string): boolean => {
  if (!message.startsWith('{') && !message.startsWith('[')) return false;
  try {
    const parsed = JSON.parse(message);
    const response = parsed && typeof parsed === 'object' ? parsed.response : null;
    return !!response && typeof response === 'object' && hasGraphQLErrors(response);
  } catch (_parseErr) {
    return false;
  }
};

/**
 * Detect if an error is network/transport-related (no usable server response).
 *
 * A response that carries a GraphQL error list or a REST body is NEVER a network
 * error, whatever words the message, query or variables happen to contain
 * (graphql-request embeds the whole request in `ClientError.message`).
 */
export function isNetworkError(error: unknown): boolean {
  if (error == null) return false;

  if (typeof error === 'object') {
    const err = error as Record<string, unknown>;

    // graphql-request ClientError / REST error: the response decides on its own
    if ('response' in err) {
      const response = err.response as Record<string, unknown>;
      if (!response || typeof response !== 'object') {
        return true;
      }
      if (hasGraphQLErrors(response) || hasRestBody(response)) {
        return false;
      }
      return true;
    }

    // graphql-request internal structure
    if ('networkError' in err) {
      return true;
    }
  }

  if (error instanceof Error) {
    if (hasSerializedServerError(error.message)) return false;

    const msg = error.message.toLowerCase();
    if (
      msg.includes('failed to fetch')
      || msg.includes('networkerror')
      || msg.includes('network error')
      || msg.includes('network')
      || msg.includes('timeout')
      || msg.includes('connection refused')
      || msg.includes('connection reset')
      || msg.includes('econnrefused')
      || msg.includes('enotfound')
      || msg.includes('econnreset')
      || msg.includes('socket')
      || msg.includes('request failed')
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Extract a GraphQL error message from a client error or serialized JSON.
 */
export function extractGraphQLMessage(error: unknown): string | null {
  if (error == null || typeof error !== 'object') {
    return null;
  }

  const err = error as Record<string, unknown>;

  // Standard graphql-request: response.errors[0].message
  if ('response' in err && err.response && typeof err.response === 'object') {
    const response = err.response as Record<string, unknown>;
    if ('errors' in response && Array.isArray(response.errors)) {
      const errors = response.errors as Array<Record<string, unknown>>;
      if (errors.length > 0 && errors[0] && typeof errors[0].message === 'string' && errors[0].message) {
        return errors[0].message as string;
      }
    }
  }

  // .message may be a serialized JSON string containing response.errors
  if ('message' in err && typeof err.message === 'string') {
    const msgStr = err.message as string;

    if (msgStr.startsWith('{') || msgStr.startsWith('[')) {
      try {
        const parsed = JSON.parse(msgStr);
        if (parsed && typeof parsed === 'object' && 'response' in parsed && parsed.response && typeof parsed.response === 'object') {
          const response = parsed.response as Record<string, unknown>;
          if ('errors' in response && Array.isArray(response.errors)) {
            const errors = response.errors as Array<Record<string, unknown>>;
            if (errors.length > 0 && errors[0] && typeof errors[0].message === 'string' && errors[0].message) {
              return errors[0].message as string;
            }
          }
        }
      } catch (_parseErr) {
        // fall through to the plain-string branch below
      }
    }

    if (msgStr && !msgStr.startsWith('{') && !msgStr.startsWith('[')) {
      return msgStr;
    }
  }

  // Alternate structures
  if ('errors' in err && Array.isArray(err.errors)) {
    const errors = err.errors as Array<Record<string, unknown>>;
    if (errors.length > 0 && errors[0] && typeof errors[0].message === 'string' && errors[0].message) {
      return errors[0].message as string;
    }
  }

  if ('graphQLErrors' in err && Array.isArray(err.graphQLErrors)) {
    const gqlErrors = err.graphQLErrors as Array<Record<string, unknown>>;
    if (gqlErrors.length > 0 && gqlErrors[0] && typeof gqlErrors[0].message === 'string' && gqlErrors[0].message) {
      return gqlErrors[0].message as string;
    }
  }

  return null;
}

/**
 * Extract a REST error message from a response-shaped error body.
 */
export function extractRestMessage(error: unknown): string | null {
  if (error == null || typeof error !== 'object') return null;

  const err = error as Record<string, unknown>;
  if (!('response' in err) || !err.response || typeof err.response !== 'object') return null;

  const response = err.response as Record<string, unknown>;
  const data = response.data as unknown;

  if (typeof data === 'string' && data && data !== '[object Object]') {
    return data;
  }

  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>;
    if (typeof body.message === 'string' && body.message) return body.message;
    if (typeof body.error === 'string' && body.error) return body.error;
    // GraphQL-over-HTTP shapes
    if (Array.isArray(body.errors) && body.errors.length > 0) {
      const first = body.errors[0] as Record<string, unknown>;
      if (first && typeof first.message === 'string' && first.message) return first.message;
    }
  }

  return null;
}

/**
 * Normalize any API error into a single human-readable string.
 * Network first, then GraphQL, then REST, then Error.message, then a generic.
 */
export type ApiErrorKind = 'network' | 'unknown' | 'message';

export const NETWORK_ERROR_MESSAGE = 'Network error. Please check your connection and try again.';
export const UNKNOWN_ERROR_MESSAGE = 'An unknown error occurred.';

export function classifyApiError(error: unknown): { kind: ApiErrorKind; message: string } {
  const unknown = { kind: 'unknown' as const, message: UNKNOWN_ERROR_MESSAGE };

  if (error == null) return unknown;

  if (isNetworkError(error)) {
    return { kind: 'network', message: NETWORK_ERROR_MESSAGE };
  }

  // REST first: extractGraphQLMessage falls back to the plain `.message`, which for a
  // generic HTTP failure ("Request failed with status code N") would hide the body.
  const serverMessage = extractRestMessage(error) || extractGraphQLMessage(error);
  if (serverMessage) return { kind: 'message', message: serverMessage };

  if (error instanceof Error) {
    return error.message ? { kind: 'message', message: error.message } : unknown;
  }

  const stringified = String(error);
  if (stringified && stringified !== '[object Object]') {
    return { kind: 'message', message: stringified };
  }

  return unknown;
}

export function normalizeApiError(error: unknown): string {
  return classifyApiError(error).message;
}

/**
 * Same as normalizeApiError, but the two fallbacks (network / unknown) are
 * translated. Real server messages are returned untouched.
 */
export function getApiErrorMessage(error: unknown, t: TFunction): string {
  const { kind, message } = classifyApiError(error);
  if (kind === 'network') return `${t('common:network-error')}`;
  if (kind === 'unknown') return `${t('common:unknown-error')}`;
  return message;
}
